import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  Bot,
  CheckCircle2,
  Coffee,
  Loader2,
  Moon,
  Sparkles,
  Sun,
  Target,
  Users,
  Calendar as CalendarIcon,
  ListChecks,
} from 'lucide-react';

import { Dialog, DialogContent } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { TimezonePicker } from '../ui/TimezonePicker';
import { settingsApi, aiApi } from '../../services/api';
import { useSettingsQuery } from '../../hooks/queries';
import { settingsKeys } from '../../lib/queryKeys';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatsForLocale, useDateTime } from '../../contexts/DateTimeContext';
import { SUPPORTED_LANGUAGES } from '../../i18n/locales';

const PERSONA_OPTIONS = [
  { id: 'bots',           icon: Bot },
  { id: 'personal_tasks', icon: ListChecks },
  { id: 'habits',         icon: Target },
  { id: 'focus',          icon: Coffee },
  { id: 'team',           icon: Users },
  { id: 'planning',       icon: CalendarIcon },
];

const HOURS_PRESETS = [
  { id: '9_5',      start: '09:00', end: '17:00' },
  { id: '8_4',      start: '08:00', end: '16:00' },
  { id: '10_6',     start: '10:00', end: '18:00' },
  { id: 'flexible', start: '00:00', end: '23:59' },
];

const detectTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

const StepDots = ({ total, current }) => (
  <div className="flex items-center gap-1.5">
    {Array.from({ length: total }).map((_, i) => (
      <span
        key={i}
        className={`h-1.5 rounded-full transition-all ${
          i === current ? 'w-6 bg-foreground' : 'w-1.5 bg-muted-foreground/30'
        }`}
      />
    ))}
  </div>
);

/* ---------------------------- Step 1: Persona ---------------------------- */
const PersonaStep = ({ value, onChange }) => {
  const { t } = useTranslation();
  const toggle = (id) => {
    if (value.includes(id)) onChange(value.filter((v) => v !== id));
    else onChange([...value, id]);
  };
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">{t('onboarding.welcome.title')}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{t('onboarding.welcome.subtitle')}</p>
      </div>
      <div className="pt-2">
        <p className="text-sm font-medium">{t('onboarding.welcome.question')}</p>
        <p className="text-xs text-muted-foreground">{t('onboarding.welcome.hint')}</p>
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {PERSONA_OPTIONS.map(({ id, icon: Icon }) => {
          const active = value.includes(id);
          return (
            <button
              key={id}
              type="button"
              onClick={() => toggle(id)}
              className={`flex items-start gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors ${
                active
                  ? 'border-foreground/40 bg-muted'
                  : 'border-border hover:bg-muted/50'
              }`}
            >
              <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <div className="text-sm font-medium">{t(`onboarding.welcome.persona.${id}`)}</div>
                <div className="text-xs text-muted-foreground">
                  {t(`onboarding.welcome.persona.${id}_desc`)}
                </div>
              </div>
              {active && <CheckCircle2 className="ml-auto h-4 w-4 shrink-0 text-foreground" />}
            </button>
          );
        })}
      </div>
    </div>
  );
};

/* --------------------------- Step 2: Work prefs --------------------------- */
const WorkPrefsStep = ({ timezone, setTimezone, hoursPreset, setHoursPreset, weekStartsOn, setWeekStartsOn }) => {
  const { t } = useTranslation();
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">{t('onboarding.work_prefs.title')}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{t('onboarding.work_prefs.subtitle')}</p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="onb-tz" className="text-sm">{t('onboarding.work_prefs.timezone')}</Label>
        {/* A list, not a text field: a typo ("Prague") would break every time shown. Short
            enough to open inside the dialog's scroll area without being cut off. */}
        <TimezonePicker inputId="onb-tz" value={timezone} onChange={(tz) => setTimezone(tz.value)} maxMenuHeight={200} />
        <p className="text-xs text-muted-foreground">{t('onboarding.work_prefs.timezone_detected')}</p>
      </div>

      <div className="space-y-2">
        <Label className="text-sm">{t('onboarding.work_prefs.hours_preset')}</Label>
        <div className="grid grid-cols-2 gap-2">
          {HOURS_PRESETS.map((p) => {
            const active = hoursPreset === p.id;
            const label = t(`onboarding.work_prefs.hours_${p.id}`);
            const desc = p.id === 'flexible' ? t('onboarding.work_prefs.hours_flexible_desc') : null;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => setHoursPreset(p.id)}
                className={`flex flex-col items-start gap-0.5 rounded-md border px-3 py-2 text-left transition-colors ${
                  active ? 'border-foreground/40 bg-muted' : 'border-border hover:bg-muted/50'
                }`}
              >
                <span className="text-sm font-medium">{label}</span>
                {desc && <span className="text-xs text-muted-foreground">{desc}</span>}
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-sm">{t('onboarding.work_prefs.week_starts_on')}</Label>
        <div className="grid grid-cols-2 gap-2">
          {[
            { id: 1, key: 'monday' },
            { id: 0, key: 'sunday' },
          ].map((opt) => {
            const active = weekStartsOn === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => setWeekStartsOn(opt.id)}
                className={`rounded-md border px-3 py-2 text-sm transition-colors ${
                  active ? 'border-foreground/40 bg-muted' : 'border-border hover:bg-muted/50'
                }`}
              >
                {t(`onboarding.work_prefs.${opt.key}`)}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/* --------------------------- Step 3: Appearance --------------------------- */
const AppearanceStep = ({ theme, setTheme, language, setLanguage }) => {
  const { t } = useTranslation();
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">{t('onboarding.appearance.title')}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{t('onboarding.appearance.subtitle')}</p>
      </div>

      <div className="space-y-2">
        <Label className="text-sm">{t('onboarding.appearance.theme')}</Label>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setTheme('light')}
            className={`flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors ${
              theme === 'light' ? 'border-foreground/40 bg-muted' : 'border-border hover:bg-muted/50'
            }`}
          >
            <Sun className="h-4 w-4" />
            {t('onboarding.appearance.theme_light')}
          </button>
          <button
            type="button"
            onClick={() => setTheme('dark')}
            className={`flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors ${
              theme === 'dark' ? 'border-foreground/40 bg-muted' : 'border-border hover:bg-muted/50'
            }`}
          >
            <Moon className="h-4 w-4" />
            {t('onboarding.appearance.theme_dark')}
          </button>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="onb-lang" className="text-sm">{t('onboarding.appearance.language')}</Label>
        <select
          id="onb-lang"
          value={language}
          onChange={(e) => setLanguage(e.target.value)}
          className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
        >
          {SUPPORTED_LANGUAGES.map((l) => (
            <option key={l.code} value={l.code}>
              {l.flag} {l.native} ({l.code.toUpperCase()})
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

/* --------------------------- Step 4: First task --------------------------- */
const FirstTaskStep = ({ added, setAdded }) => {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!text.trim() || busy || added) return;
    setBusy(true);
    setError(null);
    try {
      await aiApi.quickAdd(text.trim(), true);
      setAdded(true);
    } catch {
      setError(t('onboarding.first_task.error'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">{t('onboarding.first_task.title')}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{t('onboarding.first_task.subtitle')}</p>
      </div>

      <div className="flex items-stretch gap-2">
        <div className="relative flex-1">
          <Sparkles className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
            placeholder={t('onboarding.first_task.placeholder')}
            disabled={busy || added}
            className="h-10 pl-8"
          />
        </div>
        <Button onClick={submit} disabled={busy || added || !text.trim()} className="h-10">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t('onboarding.first_task.try_it')}
        </Button>
      </div>

      {added && (
        <div className="flex items-center gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm">
          <CheckCircle2 className="h-4 w-4 text-emerald-500" />
          {t('onboarding.first_task.success')}
        </div>
      )}
      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}
    </div>
  );
};

/* ----------------------------- Main modal ------------------------------ */
const OnboardingModal = ({ open, onClose }) => {
  const { t } = useTranslation();
  const settingsQuery = useSettingsQuery({ enabled: open });
  const queryClient = useQueryClient();
  const { theme, setTheme } = useTheme();
  const { language, setLanguage } = useLanguage();
  const { updateSettings: updateDateTime } = useDateTime();

  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  // Step 1
  const [persona, setPersona] = useState([]);
  // Step 2
  const initialTz = useMemo(() => detectTimezone(), []);
  const [timezone, setTimezone] = useState(initialTz);
  const [hoursPreset, setHoursPreset] = useState('9_5');
  const [weekStartsOn, setWeekStartsOn] = useState(1);
  // Step 4
  const [taskAdded, setTaskAdded] = useState(false);

  // Sync timezone/week defaults from existing settings (if user has any).
  useEffect(() => {
    const s = settingsQuery.data;
    if (!s) return;
    if (s.timezone && s.timezone !== 'UTC') setTimezone(s.timezone);
    if (typeof s.week_starts_on === 'number') setWeekStartsOn(s.week_starts_on);
  }, [settingsQuery.data]);

  const totalSteps = 4;

  const persist = async ({ skipped = false } = {}) => {
    setSubmitting(true);
    try {
      const preset = HOURS_PRESETS.find((p) => p.id === hoursPreset) || HOURS_PRESETS[0];
      await settingsApi.completeOnboarding({
        persona: skipped ? [] : persona,
        working_hours: skipped
          ? null
          : { start: preset.start, end: preset.end, days: [0, 1, 2, 3, 4] },
        timezone: skipped ? null : timezone,
        week_starts_on: skipped ? null : weekStartsOn,
        theme: skipped ? null : theme,
        language: skipped ? null : language,
        skipped,
      });
      // Dates and times as people of the chosen language (and the browser's country, when it
      // speaks that language) write them, not the US default; through the date context so
      // the app shows the new timezone, week and formats without a reload.
      const browser = typeof navigator !== 'undefined' ? navigator.language || '' : '';
      const formats = formatsForLocale(browser.toLowerCase().startsWith(language) ? browser : language);
      await updateDateTime({
        ...(formats || {}),
        ...(skipped ? {} : { timezone, weekStartsOn }),
      });
      await queryClient.invalidateQueries({ queryKey: settingsKeys.current() });
    } catch (e) {
      console.error('Failed to persist onboarding', e);
    } finally {
      setSubmitting(false);
      onClose?.();
    }
  };

  const next = () => {
    if (step < totalSteps - 1) setStep(step + 1);
    else persist();
  };
  const back = () => setStep(Math.max(0, step - 1));
  const skip = () => persist({ skipped: true });

  // Persistent dialog: user can only close via Finish or Skip.
  const handleOpenChange = (next) => {
    if (!next && !submitting) skip();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-lg gap-0 p-0 [&>button]:hidden flex flex-col max-h-[min(var(--vvh,100dvh),92dvh)] md:max-h-[85vh]"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <div className="flex items-center justify-between border-b px-5 py-3 shrink-0">
          <StepDots total={totalSteps} current={step} />
          <button
            type="button"
            onClick={skip}
            disabled={submitting}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            {t('onboarding.skip')}
          </button>
        </div>

        <div className="overflow-y-auto flex-1 min-h-0 px-5 py-5">
          {step === 0 && <PersonaStep value={persona} onChange={setPersona} />}
          {step === 1 && (
            <WorkPrefsStep
              timezone={timezone}
              setTimezone={setTimezone}
              hoursPreset={hoursPreset}
              setHoursPreset={setHoursPreset}
              weekStartsOn={weekStartsOn}
              setWeekStartsOn={setWeekStartsOn}
            />
          )}
          {step === 2 && (
            <AppearanceStep
              theme={theme}
              setTheme={setTheme}
              language={language}
              setLanguage={setLanguage}
            />
          )}
          {step === 3 && <FirstTaskStep added={taskAdded} setAdded={setTaskAdded} />}
        </div>

        <div className="flex items-center justify-between border-t px-5 py-3 shrink-0 bg-background">
          <Button
            variant="ghost"
            onClick={back}
            disabled={submitting}
            // Hidden on the first step (nothing to go back to). `invisible`
            // rather than removing it keeps the step counter centered and the
            // Next button in place, so the footer doesn't shift on step 2.
            className={`h-8${step === 0 ? ' invisible' : ''}`}
          >
            {t('onboarding.back')}
          </Button>
          <div className="text-xs text-muted-foreground">
            {t('onboarding.step_of', { current: step + 1, total: totalSteps })}
          </div>
          <Button onClick={next} disabled={submitting} className="h-8">
            {submitting && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            {step === totalSteps - 1 ? t('onboarding.finish') : t('onboarding.next')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingModal;
